import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Signup() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setError("Email is required");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      setLoading(false);

      if (error) {
        setError(error.message);
        return;
      }

      setEmail("");
      setPassword("");
      setConfirmPassword("");

      setSuccess("Signup successful! Check your email to confirm.");
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } catch (err) {
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-white px-6 py-10 sm:py-14 flex items-center justify-center"
      style={{
        paddingTop: "max(2.5rem, env(safe-area-inset-top))",
        paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-6xl md:text-7xl font-semibold tracking-tight mb-6 pb-2 logo-gradient">
            Niyyah
          </h1>
          <p className="text-sm text-niyyah-muted font-light mb-2">
            Begin your journey
          </p>
          <p className="text-xs text-niyyah-muted/70 font-light max-w-sm mx-auto">
            Create an account to find meaningful connections
          </p>
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <motion.div
                className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center success-icon-gradient"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>

              <h2 className="text-2xl font-light text-niyyah-text mb-3">
                Welcome to Niyyah
              </h2>
              <p className="text-sm text-niyyah-muted font-light">{success}</p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleSignup}
              className="space-y-6"
            >
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs text-niyyah-muted font-medium mb-2 uppercase tracking-wider"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full p-4 text-sm text-niyyah-text font-light bg-white border border-gray-200 rounded-xl focus:border-niyyah-light focus:ring-1 focus:ring-niyyah-light/20 outline-none placeholder:text-gray-400 transition-all"
                  disabled={loading}
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-xs text-niyyah-muted font-medium mb-2 uppercase tracking-wider"
                >
                  Password{" "}
                  <span className="font-normal text-niyyah-muted/60">
                    (min 6 characters)
                  </span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full p-4 text-sm text-niyyah-text font-light bg-white border border-gray-200 rounded-xl focus:border-niyyah-light focus:ring-1 focus:ring-niyyah-light/20 outline-none placeholder:text-gray-400 transition-all"
                  disabled={loading}
                  required
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-xs text-niyyah-muted font-medium mb-2 uppercase tracking-wider"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full p-4 text-sm text-niyyah-text font-light bg-white border border-gray-200 rounded-xl focus:border-niyyah-light focus:ring-1 focus:ring-niyyah-light/20 outline-none placeholder:text-gray-400 transition-all"
                  disabled={loading}
                  required
                />
              </div>

              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="text-xs text-red-500 font-light text-center"
                  >
                    {error}
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Sign Up Button - Circular */}
              <div className="flex justify-center pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="auth-button-circle"
                >
                  <span className="text-sm font-light leading-tight relative z-10">
                    {loading ? "Signing up..." : "Sign Up"}
                  </span>
                </button>
              </div>

              {/* Login link */}
              <div className="text-center pt-4">
                <p className="text-sm text-niyyah-muted font-light">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-niyyah-purple hover:text-niyyah-purple/80 font-medium transition-colors"
                  >
                    Log in
                  </Link>
                </p>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Back link */}
        <div className="text-center mt-10 sm:mt-12">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-niyyah-muted hover:text-niyyah-purple transition-colors font-light inline-flex items-center gap-1"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to homepage
          </button>
        </div>
      </motion.div>

      <style>{`
        .logo-gradient {
          background-image:
            url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
            linear-gradient(
              135deg,
              #7a6ba8 0%,
              #8b7db8 12%,
              #9b8fc4 25%,
              #8c9ed8 38%,
              #7ba5d9 50%,
              #6bc4e0 62%,
              #b89bc8 75%,
              #9b8fc4 88%,
              #7a6ba8 100%
            );
          background-size:
            200px 200px,
            400% 400%;
          background-blend-mode: overlay, normal;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: logoGradientFluctuate 12s ease-in-out infinite;
        }

        @keyframes logoGradientFluctuate {
          0%,
          100% {
            background-position:
              0px 0px,
              0% 50%;
          }
          25% {
            background-position:
              0px 0px,
              100% 50%;
          }
          50% {
            background-position:
              0px 0px,
              50% 100%;
          }
          75% {
            background-position:
              0px 0px,
              0% 100%;
          }
        }

        .auth-button-circle {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #9b8fc4;
          border: none;
          cursor: pointer;
        }

        .auth-button-circle::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .auth-button-circle:hover:not(:disabled) {
          transform: scale(1.05);
        }

        .auth-button-circle:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .success-icon-gradient {
          background-image:
            url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
            linear-gradient(
              135deg,
              #7a6ba8 0%,
              #8b7db8 12%,
              #9b8fc4 25%,
              #8c9ed8 38%,
              #7ba5d9 50%,
              #6bc4e0 62%,
              #b89bc8 75%,
              #9b8fc4 88%,
              #7a6ba8 100%
            );
          background-size:
            200px 200px,
            400% 400%;
          background-blend-mode: overlay, normal;
          animation: logoGradientFluctuate 12s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}